import { useState, useEffect, useMemo } from 'react';
import { Check, ChevronLeft, ChevronRight, Activity, Info } from 'lucide-react';
import DashboardLayout from './DashboardLayout';
import { fetchActivities } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { formatActivityDetails, formatActivitySender, processActivities } from '../../utils/activity';

const ActivitiesPage = () => {
  const { t } = useTranslation();
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);

  const processedActivities = useMemo(() => {
    return processActivities(activities, {
      received: t('activities.received', '收到'),
      replied: t('activities.replied', '回复'),
    });
  }, [activities, t]);

  useEffect(() => {
    const loadActivities = async () => {
      setLoading(true);
      try {
        const skip = (page - 1) * pageSize;
        const data = await fetchActivities(pageSize, skip);
        if (data && data.result) {
          setActivities(data.result);
          setTotal(data.total || 0);
        }
      } catch (error) {
        console.error('Error fetching activities:', error);
      } finally {
        setLoading(false);
      }
    };
    loadActivities();
  }, [page, pageSize]);

  const totalPages = Math.ceil(total / pageSize);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  return (
    <DashboardLayout 
      isPlanModalOpen={isPlanModalOpen}
      onPlanModalClose={() => setIsPlanModalOpen(false)}
      onOpenPlanModal={() => setIsPlanModalOpen(true)}
    >
      <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Activity className="h-5 w-5 mr-2" />
          {t('activities.title', '动态')}
        </h2>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto min-h-[400px]">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('activities.table.time', '时间')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('activities.table.type', '类型')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('activities.table.alias', '收件人')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('activities.table.sender', '发件人')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('activities.table.recipient', '真实邮箱')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {processedActivities.map((activity, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(activity.occurred_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center gap-1.5">
                          {activity._hasCheck && <Check className="h-4 w-4 text-green-500 mr-1" />}
                          {activity._displayLabel}
                          {activity.meta && (
                            <span
                              title={formatActivityDetails(activity)}
                              aria-label={t('activities.table.details', '详情')}
                              className="inline-flex"
                            >
                              <Info className="h-4 w-4 text-gray-400" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {activity.alias_email || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatActivitySender(activity)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {activity.direction === 'inbound' ? (activity.delivered_to_email || '-') : 
                         activity.direction === 'outbound' ? (activity.replied_to_email || '-') : '-'}
                      </td>
                    </tr>
                  ))}
                  {processedActivities.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">{t('activities.empty', '暂无动态')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="flex items-center">
                <span className="text-sm text-gray-700 mr-2">{t('activities.showPerPage', '每页显示:')}</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="block w-full rounded-md border-gray-300 py-1.5 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                >
                  <option value={10}>10</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-700 ml-4">
                  {t('activities.total', { total })}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-700">
                  {t('activities.pagination', { page, totalPages: totalPages || 1 })}
                </span>
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ActivitiesPage;
